import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import SeoHead from '../components/SeoHead';
import { FOR_ENTERPRISE_SEO } from '../config/forEnterpriseSeo';
import { BOOKING_CALENDAR_SRC } from '../config/bookingCalendar';
import Modal from '../components/common/Modal';
import { BRAND_MARK_SRC } from '../config/brandMark.generated';

// ============================================================================
// Animations & Layout Base
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 3rem;
  overflow-x: hidden;
  width: 100%;
`;

const TwoColumnLayout = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 8rem 2rem 6rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 6rem 1.25rem 4rem;
    gap: 3rem;
  }
`;

const Hero = styled.section`
  animation: ${fadeIn} 0.45s ease both;
`;

const Eyebrow = styled.p`
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: var(--brand-text-muted);
  margin-bottom: 0.5rem;
  text-transform: lowercase;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.15;
  margin-bottom: 1rem;
`;

const HeroSub = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.1rem);
  color: #666;
  line-height: 1.6;
  margin: 0 0 2rem;
`;

/**
 * Was a <blockquote> carrying an unattributed testimonial claiming a 60%
 * reduction in research and drafting time (removed 2026-09-11). Nobody was
 * attached to it and nothing substantiated the number — 16 CFR 465, in force
 * since October 2024, reaches a testimonial that does not reflect a real
 * consumer's experience, and the FTC's substantiation rules reach the
 * statistic separately.
 *
 * It is no longer a blockquote, because what stands here now is our own
 * description of the product rather than a customer's words, and our own
 * marketing set inside quotation marks is the misrepresentation in miniature.
 * If a real, consented, sourced quote arrives, restore the blockquote with it
 * and add the sourcing record to config/marketing-claims.json.
 */
const HeroNote = styled.p`
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  color: #555;
  border-left: 2px solid var(--brand-gold);
  padding-left: 1rem;
  margin-bottom: 1.25rem;
`;

const RightColumn = styled.div`
  animation: ${fadeIn} 0.55s 0.1s ease both;
  background: #FFF;
  border-radius: 1.5rem;
  border: 1.5px solid var(--brand-text);
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const ActionButton = styled.button`
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  width: 100%;
  margin-top: 1.5rem;
  transition: background 0.15s ease;

  &:hover { background: #2a2a2a; }
`;

const ForEnterprise: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAction = () => {
    setIsModalOpen(true);
  };

  return (
    <Page>
      {/* One copy of what this page tells search, shared with its rebuild (src/config/forEnterpriseSeo.ts). */}
      <SeoHead {...FOR_ENTERPRISE_SEO} />


      <TwoColumnLayout>
        <div>
          <Hero>
            <Eyebrow>Law · Finance</Eyebrow>
            <HeroTitle>AI employees for your vertical</HeroTitle>
            <HeroSub>Deploy a dedicated AI employee for law or finance — private infrastructure, deep workflow support, and go-live in weeks, not quarters.</HeroSub>
            <HeroNote>
              Your firm gets its own dedicated machine and workspace — not a shared tenant.
              Prest0n reads the matters you give it, drafts from your templates, and works
              inside the systems you already use.
            </HeroNote>
          </Hero>
        </div>

        <RightColumn>
          <img src={BRAND_MARK_SRC} alt="prest0 logo" width="80" style={{marginBottom: '2rem'}} />
          <h2 style={{fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--brand-text)', fontFamily: 'Space Grotesk'}}>Ready to get started?</h2>
          <p style={{color: '#666', marginBottom: '1rem', fontSize: '0.95rem'}}>
            Experience how prest0 is changing the legal landscape for you.
          </p>
          <ActionButton onClick={handleAction}>
            Talk to sales
          </ActionButton>
        </RightColumn>
      </TwoColumnLayout>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Book a demo"
        size="xlarge"
        maxWidth="1100px"
        footer={null}
      >
        <div style={{ height: '600px', width: '100%' }}>
          <iframe
            title="Book a Demo"
            src={BOOKING_CALENDAR_SRC}
            style={{ border: 'none', width: '100%', height: '100%' }}
          ></iframe>
        </div>
      </Modal>
    </Page>
  );
};

export default ForEnterprise;
